import { useMemo } from 'react';
import type { IValidatorContext } from './context';
import { ValidatorContext } from './context';
import { checkIfValid } from './helpers';
import { useValidate } from './hooks/internal/useValidate';
import type { IValidatorRef } from './hooks/internal/useValidatorRef';
import { useValidatorRef } from './hooks/internal/useValidatorRef';
import type { ICommonValidator, IValidationSchema } from './types';
import type { IValidateParams } from './utils/validate/types';

export type IValidationParams = {
  validateOnChange?: boolean;
  validationDelay?: number;
  abortEarly?: boolean;
} & IValidateParams;

export type IValidatorProviderProps<TValue extends object> = {
  children: React.ReactNode | React.ReactNode[];
  schema: IValidationSchema[];
  globalValidationRules?: Array<ICommonValidator<TValue, string>>;
  value: TValue;

  ref?: React.RefObject<IValidatorRef>;
} & IValidationParams;

export const ValidatorProvider = <TValue extends object>({
  children,
  schema,
  value,
  validateOnChange,
  abortEarly,
  validationDelay,
  abortAfterFirstError,
  globalValidationRules,
  ref,
}: IValidatorProviderProps<TValue>) => {
  useValidatorRef(ref);
  const { errors, validate } = useValidate(value, schema, {
    abortEarly,
    validateOnChange,
    validationDelay,
    abortAfterFirstError,
    globalValidationRules,
  });

  const context: IValidatorContext<TValue> = useMemo(
    () => ({ errors, values: value, isValid: checkIfValid(errors), validate }),
    [errors, value, validate],
  );

  return <ValidatorContext.Provider value={context}>{children}</ValidatorContext.Provider>;
};
